import { useCallback, useRef, useState } from 'react'

export interface EditingCell {
  rowId: string
  field: string
  /**
   * The editor should open its list straight away. Set only by a click on a
   * dropdown cell's caret — arriving by keyboard, or clicking the value
   * itself, opens the editor without the list covering the rows below.
   */
  openList?: boolean
}

/**
 * Single-click cell editing.
 *
 * Replaces `src/utils/agGridEditing.ts`, which existed to work around ag-grid's
 * `singleClickEdit` not reopening the editor on an already-FOCUSED cell — a cell
 * left focused after dismissing a dropdown needed a second click, which read as
 * "this column needs a double click". Here there is no separate focus state to
 * get stuck on: a click on an editable cell opens its editor, full stop.
 *
 * Two clicks are still left alone. A click inside the cell that is already
 * editing is the user placing the caret, and restarting the editor would
 * reselect the value and force a retype. A click on a control a cell renderer
 * painted inside the cell and marked `data-no-cell-edit` is meant to do its own
 * job, not open the editor behind it.
 */
export const useEditing = () => {
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null)
  const editingRef = useRef<EditingCell | null>(null)
  editingRef.current = editingCell

  const startEditing = useCallback(
    (rowId: string, field: string, openList = false) => {
      setEditingCell({ rowId, field, openList })
    },
    [],
  )

  const stopEditing = useCallback(() => {
    setEditingCell(null)
  }, [])

  const isEditing = useCallback(
    (rowId: string, field: string) =>
      editingRef.current?.rowId === rowId && editingRef.current?.field === field,
    [],
  )

  /** Any cell at all, read through a ref so a caller need not re-render. */
  const isEditingAny = useCallback(() => editingRef.current != null, [])

  /** The open cell, read through the same ref. */
  const getEditingAny = useCallback(() => {
    const cell = editingRef.current
    return cell ? { rowId: cell.rowId, field: cell.field } : null
  }, [])

  const handleCellClick = useCallback(
    (
      event: React.MouseEvent,
      rowId: string,
      field: string,
      editable: boolean,
      openList = false,
    ) => {
      const target = event.target as HTMLElement | null
      if (target?.closest?.('[data-no-cell-edit]')) return
      if (!editable) return
      if (editingRef.current?.rowId === rowId && editingRef.current?.field === field) {
        return
      }
      setEditingCell({ rowId, field, openList })
    },
    [],
  )

  return {
    editingCell,
    startEditing,
    stopEditing,
    isEditing,
    isEditingAny,
    getEditingAny,
    handleCellClick,
  }
}
